import React, { useState, useEffect } from 'react';
import { Bell, Check, CheckCheck, Filter } from 'lucide-react';
import { adminAlertsService } from '../../services/adminAlertsService';
import { realtimeService } from '../../services/realtimeService';
import { useNavigate } from 'react-router-dom';

export default function AdminNotifications() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [selectedAlerts, setSelectedAlerts] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    loadAlerts();
  }, [page, showUnreadOnly, filterType]);

  // Subscribe to real-time alert updates
  useEffect(() => {
    const subscription = realtimeService?.subscribeToAdminAlerts((payload) => {
      if (payload?.eventType === 'INSERT') {
        const newAlert = payload?.new;
        setAlerts(prev => [newAlert, ...prev]);
      }
    });

    return () => {
      realtimeService?.unsubscribe('admin-alerts-realtime');
    };
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const { alerts: data, totalPages: pages } = await adminAlertsService?.getAlerts({
        page,
        limit: 20,
        unreadOnly: showUnreadOnly
      });

      let filteredData = data;
      if (filterType !== 'all') {
        filteredData = data?.filter(alert => alert?.type === filterType);
      }

      setAlerts(filteredData);
      setTotalPages(pages);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      await adminAlertsService?.markAsRead(alertId);
      setAlerts(prev => prev?.map(alert =>
        alert?.id === alertId ? { ...alert, is_read: true } : alert
      ));
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await adminAlertsService?.markAllAsRead();
      setAlerts(prev => prev?.map(alert => ({ ...alert, is_read: true })));
      setSelectedAlerts(new Set());
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleMarkSelectedAsRead = async () => {
    try {
      await adminAlertsService?.markMultipleAsRead(Array.from(selectedAlerts));
      setAlerts(prev => prev?.map(alert =>
        selectedAlerts?.has(alert?.id) ? { ...alert, is_read: true } : alert
      ));
      setSelectedAlerts(new Set());
    } catch (error) {
      console.error('Error marking selected as read:', error);
    }
  };

  const handleAlertClick = (alert) => {
    if (!alert?.is_read) {
      handleMarkAsRead(alert?.id);
    }

    if (alert?.entity_type && alert?.entity_id) {
      switch (alert?.entity_type) {
        case 'container':
          navigate(`/admin/containers/${alert?.entity_id}`);
          break;
        case 'shipment':
          navigate(`/admin/shipments/${alert?.entity_id}`);
          break;
        case 'pickup_task': navigate('/admin/pickup-tasks');
          break;
        case 'delivery_task': navigate('/admin/delivery-tasks');
          break;
        default:
          break;
      }
    }
  };

  const toggleAlertSelection = (alertId) => {
    setSelectedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(alertId)) {
        newSet?.delete(alertId);
      } else {
        newSet?.add(alertId);
      }
      return newSet;
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'border-red-200 bg-red-50';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50';
      case 'info':
      default:
        return 'border-blue-200 bg-blue-50';
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'info':
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const alertTypes = [
    { value: 'all', label: 'Tous' },
    { value: 'container_status', label: 'Statut conteneur' },
    { value: 'task_assigned', label: 'Tâche assignée' },
    { value: 'shipment_created', label: 'Envoi créé' },
    { value: 'shipment_updated', label: 'Envoi mis à jour' },
    { value: 'notification_failed', label: 'Échec notification' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Notifications Admin</h1>
              <p className="mt-2 text-gray-600">
                Gérez toutes vos notifications système
              </p>
            </div>
            <div className="flex items-center gap-4">
              {selectedAlerts?.size > 0 && (
                <button
                  onClick={handleMarkSelectedAsRead}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Check className="w-4 h-4" />
                  Marquer sélection comme lu ({selectedAlerts?.size})
                </button>
              )}
              <button
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                <CheckCheck className="w-4 h-4" />
                Tout marquer comme lu
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              {alertTypes?.map(type => (
                <option key={type?.value} value={type?.value}>
                  {type?.label}
                </option>
              ))}
            </select>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showUnreadOnly}
                onChange={(e) => setShowUnreadOnly(e?.target?.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Non lus uniquement
            </label>
          </div>
        </div>

        {/* Alerts List */}
        <div className="space-y-3">
          {loading ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
              Chargement des notifications...
            </div>
          ) : alerts?.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucune notification à afficher</p>
            </div>
          ) : (
            alerts?.map((alert) => (
              <div
                key={alert?.id}
                className={`bg-white rounded-lg shadow-sm border-l-4 ${getSeverityColor(alert?.severity)} ${
                  !alert?.is_read ? 'border-l-blue-600' : 'border-l-gray-300'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      checked={selectedAlerts?.has(alert?.id)}
                      onChange={() => toggleAlertSelection(alert?.id)}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    
                    <div
                      onClick={() => handleAlertClick(alert)}
                      className="flex-1 cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`text-lg font-semibold ${
                              !alert?.is_read ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {alert?.title}
                            </h3>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getSeverityBadge(alert?.severity)}`}>
                              {alert?.severity}
                            </span>
                            {!alert?.is_read && (
                              <span className="w-2 h-2 bg-blue-600 rounded-full" />
                            )}
                          </div>
                          <p className="text-gray-600 mb-2">{alert?.message}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span>
                              {new Date(alert.created_at)?.toLocaleString('fr-FR', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {alert?.entity_type && (
                              <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                                {alert?.entity_type}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {!alert?.is_read && (
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleMarkAsRead(alert?.id);
                            }}
                            className="text-blue-600 hover:text-blue-700 p-2"
                            title="Marquer comme lu"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Précédent
            </button>
            <span className="text-sm text-gray-600">
              Page {page} sur {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          </div>
        )}
      </div>
    </div>
  );
}